// communicatie media — the binary + write calls the generic Refine dataProvider can't make.
// Uploads are multipart and downloads are bytes, and the access token is bearer-only (in memory, not a
// cookie), so a plain <img src>/<a href> can't carry it — every call attaches the bearer here and the
// thumbnail / insert-from-library flows go through an authenticated blob fetch → object URL (or a
// base64 data: URI for the editor insert). Mirrors the animal media tile pattern (src/api/media.ts).
import { getAccessToken } from "../../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** The 201 DTO returned by POST communicatie/media (matches the GET communicatie/media row shape). */
export interface CommMediaDto {
  id: string;
  name: string;
  fileRef: string | null;
  fileName: string | null;
  contentType: string | null;
  fileSizeBytes: number | null;
  mediaType: string | null;
  usage: string | null;
  vestigingId: number | null;
  defaultWidthPx: number | null;
  createdDate: string | null;
  createdByUser: string | null;
}

/** An HTTP failure carrying the status code so callers can branch (e.g. surface a clear message). */
export class CommMediaApiError extends Error {
  readonly statusCode: number;
  constructor(statusCode: number, message?: string) {
    super(message ?? `HTTP ${statusCode}`);
    this.statusCode = statusCode;
    this.name = "CommMediaApiError";
  }
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Werk de gegevens van een media-item bij. Het bestand blijft ongemoeid — een andere afbeelding is
 * een nieuwe upload. Zonder deze aanroep kon een verkeerd getypte naam alleen worden rechtgezet door
 * het item te verwijderen, en dan verliest elk sjabloon dat ernaar verwijst zijn afbeelding.
 */
export async function bewerkCommMedia(
  id: string,
  velden: { name: string; mediaType: string; usage: string; defaultWidthPx: number | null },
): Promise<CommMediaDto> {
  const res = await fetch(`${API_URL}/communicatie/media/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(velden),
  });
  if (!res.ok) throw new CommMediaApiError(res.status, res.statusText);
  return (await res.json()) as CommMediaDto;
}

/** Eén veld dat in een overgenomen document is herkend. */
export interface HerkendVeld {
  token: string;
  label: string;
  /** De tekst die op die plek in het originele document stond — waarmee je de gok kunt nakijken. */
  gevondenTekst: string | null;
}

/** Het voorstel dat uit een overgenomen document komt; niets hiervan is opgeslagen. */
export interface DocumentVoorstel {
  beschikbaar: boolean;
  bodyHtml: string | null;
  velden: HerkendVeld[];
  /** Velden die het model verzon en die de api heeft tegengehouden. */
  onbekend: string[];
  /**
   * De blokken die per dier of per kostenregel worden herhaald ("Dier", "Kostenregel"). Ze staan
   * één keer in het sjabloon en worden bij het afdrukken zo vaak neergezet als nodig.
   */
  herhaalblokken: string[];
  paginas: number;
  melding: string | null;
  /** Wat de server onderweg zag: bestand, pagina-renders met hun inktpercentage, en het antwoord. */
  diagnose: string | null;
}

/**
 * Neem een bestaand document (pdf, scan of Word) over als sjabloon. Slaat niets op: het antwoord is
 * een voorstel dat de editor naast het origineel toont.
 */
export async function importeerDocument(bestand: File): Promise<DocumentVoorstel> {
  const form = new FormData();
  form.append("file", bestand);

  const res = await fetch(`${API_URL}/communicatie/templates/import-document`, {
    method: "POST",
    headers: authHeaders(), // geen Content-Type — de browser zet de multipart-grens
    body: form,
  });
  if (!res.ok) throw new CommMediaApiError(res.status, res.statusText);
  return (await res.json()) as DocumentVoorstel;
}

/** Upload one media file with its name + classification → 201 created DTO. RELATIVE path under /api. */
export async function uploadCommMedia(
  file: File,
  meta: { name: string; mediaType: string; usage: string },
): Promise<CommMediaDto> {
  const form = new FormData();
  form.append("file", file);
  form.append("name", meta.name);
  form.append("mediaType", meta.mediaType);
  form.append("usage", meta.usage);

  const res = await fetch(`${API_URL}/communicatie/media`, {
    method: "POST",
    headers: authHeaders(), // no Content-Type — the browser sets the multipart boundary
    body: form,
  });
  if (!res.ok) throw new CommMediaApiError(res.status, res.statusText);
  return (await res.json()) as CommMediaDto;
}

/** Hard-delete a media item → 204. */
export async function deleteCommMedia(id: string): Promise<void> {
  const res = await fetch(`${API_URL}/communicatie/media/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new CommMediaApiError(res.status, res.statusText);
}

/** Fetch a media item's bytes (authenticated) as a Blob — for thumbnails. */
export async function fetchCommMediaBlob(id: string): Promise<Blob> {
  const res = await fetch(`${API_URL}/communicatie/media/${id}/content`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new CommMediaApiError(res.status, res.statusText);
  return res.blob();
}

/**
 * Fetch a media item's bytes and encode them as a self-contained base64 `data:` URI. The editor inserts
 * the image as a data: URI rather than a relative authed URL because a data: URI renders everywhere the
 * body travels — the editor, the sandboxed preview iframe, AND the server-side PDF (which already handles
 * data: images for signatures). A relative authed URL would NOT carry the bearer server-side. Accepted
 * MVP tradeoff: large images inflate the stored bodyHtml.
 */
export async function fetchCommMediaDataUri(id: string): Promise<string> {
  const blob = await fetchCommMediaBlob(id);
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}

/**
 * De afdrukstylesheet voor de documentweergave van een sjabloon met vaste opmaak.
 *
 * <p>Dezelfde stylesheet die de pdf-renderer gebruikt, opgehaald bij de server in plaats van hier
 * meegebakken: zo kunnen de editor en het afgedrukte document niet uit elkaar lopen. Hij zit achter de
 * bearer, dus een `<link>` in het iframe zou hem niet krijgen — vandaar ophalen en meegeven als tekst.
 */
export async function fetchPrintCss(): Promise<string> {
  const res = await fetch(`${API_URL}/communicatie/templates/print-css`, { headers: authHeaders() });
  if (!res.ok) throw new CommMediaApiError(res.status, res.statusText);
  return res.text();
}
